import { gql } from "@apollo/client";
import { ButtonBase } from "@mui/material";
import Stack from "atoms/Stack";
import { COLOR_GREY_BORDER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING_DENSE, SPACING_DENSE_HALF, SPACING_HALF } from "helpers/spacings";
import usePseudoStyle from "helpers/usePseudoStyle";
import GamePlayerAvatar, { GamePlayerAvatarFragment } from "molecules/GamePlayerAvatar";
import React from "react";

export const GamePlayerSelectorFragment = gql`
  fragment GamePlayerSelectorFragment on Game {
    id
    home {
      id
      name
    }
    away {
      id
      name
    }
    gamePlayers {
      id
      isHomeTeamPlayer
      ...GamePlayerAvatarFragment
      person {
        id
        fullNameWithAnonymisation
      }
      team {
        id
      }
    }
  }
  ${GamePlayerAvatarFragment}
`;

export default function GamePlayerSelector({ gamePlayerIdSet, game, gamePlayerId }) {
  const teams = game && [game.home, game.away];

  return (
    <>
      <Stack horizontal>
        {teams?.map((team) => (
          <div
            key={team.id}
            style={{
              display: "flex",
              flexFlow: "column nowrap",
              alignItems: "stretch",
              minWidth: 200,
              maxWidth: 220,
            }}
          >
            <div
              style={{
                padding: `${SPACING_HALF} ${SPACING_DENSE}`,
                borderBottom: `1px solid ${COLOR_GREY_BORDER}`,
                fontFamily: FONT_FAMILY_GRAPHIK,
                fontSize: "0.8em",
                height: 36,
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {team.name}
            </div>
            {game?.gamePlayers
              .filter((gp) => gp.team.id === team.id)
              .map((gamePlayer) => (
                <GamePlayerSelectorItem
                  key={gamePlayer.id}
                  selected={gamePlayer.id === gamePlayerId}
                  onClick={() => {
                    gamePlayerIdSet(gamePlayer.id);
                  }}
                  gamePlayer={gamePlayer}
                />
              ))}
          </div>
        ))}
      </Stack>
    </>
  );
}

export function GamePlayerSelectorItem({ gamePlayer, selected = false, onClick }) {
  const selectedColor = "#e0137a";
  const selectedBackground = "rgb(254,246,247)";
  const hoverBackground = "#f5f5f5";
  const { className } = usePseudoStyle({
    ":hover": `font-weight: 500 !important; background-color: ${hoverBackground} !important;`,
  });

  return (
    <ButtonBase
      className={className}
      onClick={onClick}
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: SPACING_DENSE,
        paddingTop: SPACING_DENSE_HALF,
        paddingBottom: SPACING_DENSE_HALF,
        paddingLeft: SPACING_DENSE,
        paddingRight: SPACING_DENSE,
        fontFamily: FONT_FAMILY_GRAPHIK,
        fontSize: "0.75em",
        borderRadius: 2,
        ...(selected && {
          fontWeight: 500,
          backgroundColor: selectedBackground,
          color: selectedColor,
        }),
      }}
    >
      <GamePlayerAvatar gamePlayer={gamePlayer} inverse={gamePlayer.isHomeTeamPlayer} style={{ fontSize: "1em" }} />
      <div
        style={{
          overflow: "hidden",
          textAlign: "left",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: 160, // Ensure this element doesn't squeeze the player number avatar
        }}
      >
        {gamePlayer.person.fullNameWithAnonymisation}
      </div>
    </ButtonBase>
  );
}
